import React, {useState} from 'react';
import {Backdrop, Box, Fade, Modal, Rating} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import toast, {Toaster} from "react-hot-toast";
import moment from 'moment';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import styles from './DetailsOrderPage.module.scss';
import {freeTimeToCome} from "../../constants/freeTimeToCome";
import {getKyivNow} from "../../utils/kyivTime";
import {cleaningRequestService} from "../../services/cleaningRequest.service";
import sendIcon from '../../constants/images/detailsOrder/send.svg';
import deleteIcon from '../../constants/images/detailsOrder/trash-orange.svg';
import techSupportIcon from '../../constants/images/detailsOrder/tech-support.svg';
import {MAIN_ROUTES} from "../../routing/main_routes";
import TriangleStepper from "../../components/TriangleStepper/TriangleStepper";
import AddressDetailsOrder from "../../components/AddressDetailsOrder/AddressDetailsOrder";
import PackageDetailsOrder from "../../components/PackageDetailsOrder/PackageDetailsOrder";
import AdditionalInfoOrder from "../../components/AdditionalInfoOrder/AdditionalInfoOrder";
import AddedPhotosCustomerOrder from "../../components/AddedPhotosCustomerOrder/AddedPhotosCustomerOrder";
import {cleaningRequestsActions} from "../../store/slices/cleaningRequestsSlice";
import {UIActions} from "../../store/slices/UISlice";
import {getDifferenceBetweenTwoDates} from "../../constants/getDifferenceBetweenTwoDates";

const DetailsOrderPage = ({order, currentStep, setCurrentStep, isOrder}) => {
    const navigate = useNavigate();

    const {state} = useLocation();

    const dispatch = useDispatch();
    const {
        status,
        historyCleaningRequests,
        activeCleaningRequestsByCurrentUser
    } = useSelector(state => state.cleaningRequests);
    const {user} = useSelector(state => state.userDetails);
    const {
        pk: id,
        address,
        city,
        request_datetime,
        get_total_price,
        cleaning_type,
        room_count,
        pano_window_count,
        reg_window_count,
        wc_count,
        services,
        present_at_home,
        comment,
        present_at_home_desc,
        photos,
        cleaner_applications,
        created_on,
        service_package,
        request_price,
        status: requestStatus
    } = order;

    const isExpired = requestStatus === 'EXPIRED';

    // Reschedule modal state (reuses the same react-calendar + time-slot picker
    // as the order-creation flow so the submitted format matches the backend).
    const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(getKyivNow());
    const [selectedTime, setSelectedTime] = useState(freeTimeToCome[0]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const refreshRequest = async () => {
        await dispatch(cleaningRequestsActions.getById({id}));
    };

    const handleReschedule = async () => {
        try {
            setIsSubmitting(true);
            const combinedDateTime = moment(new Date(selectedDate)).set({
                hour: selectedTime?.split(':')[0],
                minute: selectedTime?.split(':')[1],
            });
            await cleaningRequestService.reschedule(id, combinedDateTime.toISOString());
            await refreshRequest();
            setIsRescheduleOpen(false);
            toast.success('Дату оновлено. Замовлення знову активне!');
        } catch (error) {
            console.error("Error rescheduling cleaning request:", error);
            toast.error(error?.response?.data?.detail || error?.message || 'Не вдалося змінити дату');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeactivate = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Ви впевнені, що хочете деактивувати це замовлення?')) {
            return;
        }
        try {
            setIsSubmitting(true);
            await cleaningRequestService.deactivate(id);
            await refreshRequest();
            toast.success(`Замовлення №${id} деактивовано.`);
        } catch (error) {
            console.error("Error deactivating cleaning request:", error);
            toast.error(error?.response?.data?.detail || error?.message || 'Не вдалося деактивувати замовлення');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteOrder = async () => {
        try {
            dispatch(UIActions.setIsLoading(true));
            await dispatch(cleaningRequestsActions.deleteById({id}));
            if (status === 'success') {
                //delete cleaning request from store
                dispatch(cleaningRequestsActions.deleteActiveCleaningRequestByCurrentUserById(id));

                navigate(MAIN_ROUTES.MAIN);
                dispatch(UIActions.setIsLoading(false));
                toast.success(`Замовлення №${id} було успішно видалено!`);

            }
        } catch (error) {
            console.error("Error deleting cleaning request:", error);
            alert(error?.message);
        }
    }

    return (
        <div className={styles.detailsOrderPage_wrapper}>
            <div className={styles.detailsOrderPage}>
                <div className={styles.detailsOrderPage_title}>
                    <h2>Прибирання звичайне та миття вікон</h2>
                    <p>{getDifferenceBetweenTwoDates(new Date(created_on), new Date().toISOString())}</p>
                    {isExpired &&
                        <span className={styles.expiredBadge}>Прострочено / клінера не знайдено</span>}
                </div>

                <TriangleStepper
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    isOrder={isOrder}
                />
                <div className={styles.detailsOrderPage_detailsOrderContainer}>
                    <div className={styles.detailsOrderPage_detailsOrderContainer_leftSide}>
                        <AddressDetailsOrder
                            date={new Date(request_datetime).toLocaleDateString()}
                            address={address}
                            price={+request_price || get_total_price}
                            time={new Date(request_datetime).toLocaleTimeString()}
                        />
                        <PackageDetailsOrder
                            style={{'padding': '24px 24px 16px 24px', 'borderBottom': '1px solid #D9DBDD'}}
                            showTitle={true}
                            typeCleaning={cleaning_type}
                            countRooms={room_count}
                            countBathrooms={wc_count}
                            countOrdinaryWindows={reg_window_count}
                            countPanoramicWindows={pano_window_count}
                            services={services}
                            selectedPackage={service_package?.name}
                        />
                        <AdditionalInfoOrder
                            isBorderBottom={photos?.length > 0}
                            present_at_home={present_at_home}
                            additionalComments={comment}
                            present_at_home_desc={present_at_home_desc}
                        />
                        {photos?.length > 0 && <AddedPhotosCustomerOrder photos={photos}/>}
                    </div>
                    <div className={styles.detailsOrderPage_detailsOrderContainer_rightSide}>
                        <div className={styles.detailsOrderPage_detailsOrderContainer_rightSide_options}>
                            {isExpired &&
                                <div className={styles.expiredActions}>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setIsRescheduleOpen(true)}
                                        className={styles.expiredActions_primary}
                                    >Змінити дату</button>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={handleDeactivate}
                                        className={styles.expiredActions_secondary}
                                    >Деактивувати</button>
                                </div>}
                            {/*<div className={styles.option}>*/}
                            {/*    <img src={edit} alt="edit icon"/>*/}
                            {/*    <p onClick={() => handleEditOrder(id)}>Редагувати замовлення</p>*/}
                            {/*</div>*/}
                            <div
                                onClick={() => setCurrentStep('step2')}
                                className={styles.option}
                            >
                                <img src={sendIcon} alt="send icon"/>
                                <p>Переглянути пропозиції</p>
                            </div>

                            {state?.type !== 'history' && <div className={styles.option}>
                                <img src={deleteIcon} alt="delete icon"/>
                                <p onClick={handleDeleteOrder}>Видалити замовлення</p>
                            </div>}

                            <div
                                className={styles.option}
                                onClick={() => navigate(MAIN_ROUTES.SUPPORT, {
                                    state: {orderId: isOrder || null, autoOpen: true}
                                })}
                            >
                                <img src={techSupportIcon} alt="tech support icon"/>
                                <p>Звʼязатися з підтримкою</p>
                            </div>
                        </div>
                        <div className={styles.detailsOrderPage_detailsOrderContainer_rightSide_clientInfo}>
                            <h3>Про клієнта</h3>
                            <div className={styles.rating}>
                                <Rating
                                    style={{fontSize: '24px'}}
                                    size="large"
                                    // name="simple-controlled"
                                    name="read-only"
                                    readOnly
                                    value={user.client_total_rating}
                                    // onChange={(event, newValue) => dispatch(feedbackCustomerActions.setQualitySelectedService2(newValue))}
                                />
                                <p>{user.client_total_rating.toFixed(2)} від клінерів</p>
                            </div>
                            <div className={styles.blockInfo}>
                                <p>Україна</p>
                                <p>{city}</p>
                            </div>
                            <div className={styles.blockInfo}>
                                <p>Зроблено {[...historyCleaningRequests, ...activeCleaningRequestsByCurrentUser]?.length} замовлень</p>
                                <p>Клієнт від {new Date(user.created_on).toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <Modal
                    aria-labelledby="reschedule-modal-title"
                    open={isRescheduleOpen}
                    onClose={() => !isSubmitting && setIsRescheduleOpen(false)}
                    closeAfterTransition
                    slots={{backdrop: Backdrop}}
                    slotProps={{backdrop: {timeout: 500}}}
                >
                    <Fade in={isRescheduleOpen}>
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            outline: 'none',
                        }}>
                            <div className={styles.rescheduleModal}>
                                <h3 id="reschedule-modal-title">Оберіть нову дату та час</h3>
                                <Calendar
                                    minDate={getKyivNow()}
                                    value={selectedDate}
                                    onChange={setSelectedDate}
                                    locale={"uk-UA"}
                                />
                                <div className={styles.rescheduleModal_times}>
                                    {freeTimeToCome.map((time) =>
                                        <button
                                            type="button"
                                            key={time}
                                            onClick={() => setSelectedTime(time)}
                                            className={`${styles.rescheduleModal_time} ${selectedTime === time ? styles.selected : ''}`}
                                        >{time}</button>)}
                                </div>
                                <div className={styles.rescheduleModal_actions}>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setIsRescheduleOpen(false)}
                                        className={styles.rescheduleModal_cancel}
                                    >Скасувати</button>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={handleReschedule}
                                        className={styles.rescheduleModal_confirm}
                                    >{isSubmitting ? 'Збереження…' : 'Підтвердити'}</button>
                                </div>
                            </div>
                        </Box>
                    </Fade>
                </Modal>

                {/* to appear hot toast notification */}
                <Toaster/>
            </div>
        </div>
    );
};

export default DetailsOrderPage;
